import { useState, useEffect, useCallback, useMemo } from "react";
import { observer } from "mobx-react-lite";
import { useQuery } from "@tanstack/react-query";
import { useTrashOperations } from "../hooks/useTrashOperations";
import { DocumentsMainView } from "./DocumentsMainView";
import { TrashView } from "./TrashView";
import { useSearchParams } from "react-router-dom";
import { useOrganizationContext } from "@/lib/context/OrganizationContext";
import { useAuth } from "@/hooks/useAuth";
import { useDocumentStore } from "@/hooks/documents/useDocumentStore";
import { useChatStore } from "@/hooks/useChatStore";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { folderService } from "../services/folderService";
import { useDocumentFilters } from "../context/DocumentFiltersContext";
import { parseTagFilters } from "../utils/parseTagFilters";
import { renameDocument, RenameDocumentValidationError } from "../repository";
import { usePermissions } from "@/hooks/usePermissions";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import type { DocumentFilters } from "@/stores/documents/DocumentStore";
import type { Document } from "@/types/documents";
import type { TreeFolder } from "../types";

interface BuildingDocumentsProps {
  buildingId: string;
  buildingName: string;
  selectedProjectId?: string | null;
  onViewTrash: () => void;
  viewMode: "list" | "timeline";
  /** Opens the Manage Shares dialog (owned by the page, which renders it). */
  onManageShares: () => void;
}

// Pagination configuration
const PAGE_SIZE = 100;

export const BuildingDocuments = observer(function BuildingDocuments({
  buildingId,
  buildingName,
  selectedProjectId,
  onViewTrash,
  viewMode,
  onManageShares,
}: BuildingDocumentsProps) {
  const [searchParams, setSearchParams] = useSearchParams();
  const showTrash = searchParams.get("view") === "trash";
  const [chatSearchResults, setChatSearchResults] = useState<{
    query: string;
    documents: any[];
  } | null>(null);

  // Authentication and organization context
  const { userProfile } = useAuth();
  const { currentOrganization, isReady } = useOrganizationContext();
  const { toast } = useToast();
  const { t } = useLanguage();
  const { canModifyData, canModifyFilesInProject, canPermanentlyDeleteDocuments } =
    usePermissions();

  // Document filters from context (includes tag filters)
  const { filters, reportResolvedSearchQuery } = useDocumentFilters();

  // Document store for lazy loading
  const documentStore = useDocumentStore();
  const chatStore = useChatStore();
  const isListView = viewMode === "list";

  // Build store filters from UI state - memoized to prevent unnecessary reloads
  // No state filter — all non-deleted documents are shown (processing docs appear immediately)
  //
  // Filter Logic:
  // - OR within each tag type (e.g., cat1 OR cat2 OR cat3)
  // - AND between different tag types (e.g., (cat1 OR cat2) AND (disc1 OR disc2))
  const storeFilters = useMemo((): DocumentFilters => {
    const baseFilters: DocumentFilters = {};

    // Apply sorting — only pass server-supported sort keys
    const serverSortKeys = ["name", "created_at", "modified_at", "user", "size"] as const;
    type ServerSortKey = (typeof serverSortKeys)[number];
    if (serverSortKeys.includes(filters.sortKey as ServerSortKey)) {
      baseFilters.sortKey = filters.sortKey as ServerSortKey;
      baseFilters.sortOrder = filters.sortOrder;
    }

    // Apply name search filter if active
    if (filters.searchQuery.trim()) {
      baseFilters.searchText = filters.searchQuery.trim();
    }

    // Global building scope: when a project is selected in the header, the
    // document list is narrowed to that project. The scope is the single source
    // of truth for the project dimension — it overrides any project from the
    // tag filter (whose Project group is locked to mirror the scope). At
    // whole-building scope (`selectedProjectId` null) no project filter applies.
    const scopeProjectId = selectedProjectId ? Number(selectedProjectId) : null;
    const scopeProjectIds =
      scopeProjectId != null && Number.isFinite(scopeProjectId) ? [scopeProjectId] : undefined;

    // When documents reached Franklin — independent of every other dimension.
    if (filters.addedDate) {
      baseFilters.uploadDateRange = filters.addedDate.range;
    }

    // Parse tag filters (project, category, discipline, stage) if any are active
    if (filters.activeTagFilters.length > 0) {
      const parsedFilters = parseTagFilters(
        filters.activeTagFilters,
        documentStore.availableFilters,
        baseFilters
      );

      // Scope wins over the tag-derived project ids (locked dimension).
      parsedFilters.projectIds = scopeProjectIds;

      return parsedFilters;
    }

    if (scopeProjectIds) {
      baseFilters.projectIds = scopeProjectIds;
    }

    return baseFilters;
  }, [
    filters.activeTagFilters,
    filters.searchQuery,
    filters.sortKey,
    filters.sortOrder,
    filters.addedDate,
    documentStore.availableFilters,
    selectedProjectId,
  ]);

  // Inlined folders query (replaces useDocumentQueries)
  const {
    data: folders = [],
    isLoading: foldersLoading,
    refetch: refetchFolders,
  } = useQuery({
    // selectedProjectId is part of the key so the tree refetches on scope change
    // and scoped/unscoped results never share a cache entry.
    queryKey: ["document-folders", buildingId, selectedProjectId ?? null],
    queryFn: async () => {
      let query = supabase
        .from("document_folders")
        .select("*")
        .eq("building_id", parseInt(buildingId, 10))
        .eq("is_deleted", false)
        .order("folder_path");
      if (selectedProjectId) query = query.eq("project_id", Number(selectedProjectId));
      const { data, error } = await query;

      if (error) throw error;

      const mappedFolders: TreeFolder[] = data.map((folder: any) => {
        const folderName = folder.name;
        const category =
          folderName.toLowerCase().includes("plan") ||
          folderName.toLowerCase().includes("detail") ||
          folderName.toLowerCase().includes("visual")
            ? "drawings"
            : "documents";

        return {
          id: String(folder.id),
          name: folderName,
          category: category,
          parentId: folder.parent_id ? String(folder.parent_id) : null,
          parent_document_id: folder.parent_document_id
            ? String(folder.parent_document_id)
            : undefined,
          isCustom: folder.is_custom || false,
          building_id: folder.building_id,
          project_id: folder.project_id || undefined,
          children: [],
        } as TreeFolder;
      });

      return folderService.buildFolderTree(mappedFolders);
    },
    // Timeline owns its own summary/document fetches and gets folder labels
    // from loadTags below. Starting this tree query there only delays the
    // Timeline mount behind data that view never renders.
    enabled: !!buildingId && isListView,
  });

  const isFoldersLoading = foldersLoading;

  // Refetch documents via store with current filters
  const refetchDocuments = useCallback(async () => {
    if (buildingId) {
      documentStore.setPageSize(PAGE_SIZE);
      await documentStore.loadDocuments(buildingId, storeFilters);
    }
  }, [buildingId, documentStore, storeFilters]);

  // Load counts and tags. All scoped to the active project so the processing
  // badge and tag chips reflect only the scoped project. The available filter
  // values are loaded by the page, which owns the Filters control for every
  // view tab (and reads back through the same store field).
  useEffect(() => {
    if (buildingId && isReady && currentOrganization && userProfile) {
      documentStore.loadDocumentCount(buildingId, selectedProjectId ?? undefined);
      documentStore.refreshProcessingCount(buildingId, selectedProjectId ?? undefined);
      documentStore.loadTags(buildingId, selectedProjectId ?? undefined);
    }
  }, [
    buildingId,
    isReady,
    currentOrganization,
    userProfile,
    documentStore,
    selectedProjectId,
    viewMode,
  ]);

  // Initial document load and reload when filters change
  // This effect triggers on filter changes to reload with server-side filtering
  //
  // Also reports the query back to the page-level search box, which sits outside
  // this component and so has no fetch of its own to watch. Reported after the
  // load settles — including on failure, since a box left spinning forever is
  // worse than one that stops next to the table's own error state.
  //
  // Only on the List tab. This component stays mounted for Timeline too (the
  // store's `documents` back selection and counts there), but the Timeline
  // renders from its own fetch and reports that one — and two reporters racing
  // would let whichever finished first stop the spinner while the rows the user
  // is looking at were still loading.
  useEffect(() => {
    if (!(isListView && buildingId && isReady && currentOrganization && userProfile)) return;

    const query = filters.searchQuery;
    let cancelled = false;

    if (isListView) reportResolvedSearchQuery(null);
    documentStore.setPageSize(PAGE_SIZE);
    documentStore
      .loadDocuments(buildingId, storeFilters)
      .catch(() => {
        // Surfaced by the table from documentStore.error.
      })
      .finally(() => {
        if (!cancelled && isListView) reportResolvedSearchQuery(query);
      });

    return () => {
      cancelled = true;
    };
  }, [
    buildingId,
    isReady,
    currentOrganization,
    userProfile,
    documentStore,
    storeFilters,
    filters.searchQuery,
    isListView,
    reportResolvedSearchQuery,
  ]);

  // Leaving the List tab hands the box back to whichever view takes over, which
  // reports null on mount until its own rows land.
  useEffect(() => {
    if (!isListView) return;
    return () => reportResolvedSearchQuery(null);
  }, [isListView, reportResolvedSearchQuery]);

  // Handle page change for pagination
  const handlePageChange = useCallback(
    (page: number) => {
      if (buildingId) {
        documentStore.goToPage(buildingId, page);
      }
    },
    [buildingId, documentStore]
  );

  // Subscribe to realtime changes
  useEffect(() => {
    if (buildingId && isReady && currentOrganization && userProfile) {
      // Which surface is live decides what a resync re-reads. This component
      // stays mounted for Timeline as well as List, and the two read from
      // completely different queries — the Folders tab shares this store and
      // sets the third — so it must say which one is on screen.
      documentStore.subscribe(
        buildingId,
        selectedProjectId ?? undefined,
        viewMode === "timeline" ? "timeline" : "list"
      );
      return () => {
        documentStore.unsubscribe();
      };
    }
    // `viewMode` decides which surface the store resyncs, so it MUST re-run on a
    // tab switch. Without it a List → Timeline switch left `activeSurface` on
    // "list", and every realtime event re-ran the paginated, tag-filtered List
    // query while the user was looking at the Timeline.
  }, [
    buildingId,
    isReady,
    currentOrganization,
    userProfile,
    documentStore,
    selectedProjectId,
    viewMode,
  ]);

  // While the store still holds the previous building's data (first render
  // after navigation, before the reload effect runs), treat it as loading and
  // hide the list so the user never sees the old building's documents flash.
  const isBuildingSwitching = documentStore.currentBuildingId !== buildingId;

  // Convert store documents to the format expected by downstream components
  const documents = isBuildingSwitching
    ? []
    : documentStore.documentsArray.map((doc) => ({
        id: doc.id,
        filename: doc.filename,
        file_path: doc.file_path,
        building_id: doc.building_id,
        parent_document_id: doc.parent_document_id,
        content_type: doc.content_type,
        size: doc.size,
        created_at: doc.created_at,
        original_created_at: doc.original_created_at,
        modified_at: doc.modified_at,
        document_content_modified_date: doc.document_content_modified_date,
        effective_modified_at: doc.effective_modified_at,
        created_by: doc.created_by,
        created_by_name: doc.created_by_name,
        created_by_avatar_url: doc.created_by_avatar_url,
        created_by_color: doc.created_by_color,
        verified_at: doc.verified_at,
        verified_by_name: doc.verified_by_name,
        state: doc.state as Document["state"],
        version: doc.version,
        version_group: doc.version_group,
        source: doc.source ?? null,
        project_id: doc.project_id,
        folder_id: doc.folder_id,
        is_deleted: doc.is_deleted,
        categories: doc.categories,
        design_disciplines: doc.design_disciplines,
        design_stages: doc.design_stages,
        linked_issues: doc.linked_issues,
      }));

  // The Timeline has an independent loading state inside
  // ServerDocumentTimeline. Gating it on the paginated List request (or the
  // List-only folder tree request) serializes the real Timeline fetch behind
  // work that it never renders.
  const isLoading =
    isListView && (isBuildingSwitching || documentStore.isLoading || isFoldersLoading);

  // Inlined document operations (replaces useDocumentOperations)
  const handleDownloadDocument = useCallback(
    async (document: Document) => {
      try {
        await documentStore.downloadDocument(document.id);
      } catch (error) {
        console.error("Download error:", error);
        toast({
          variant: "destructive",
          title: "Download failed",
          description: "Could not download the document",
        });
      }
    },
    [documentStore, toast]
  );

  const handleRenameDocument = useCallback(
    async (documentId: string, newFilename: string) => {
      const numericId = parseInt(documentId, 10);
      if (Number.isNaN(numericId)) return;

      try {
        await renameDocument(numericId, newFilename);
        // Patch in-memory caches so the row label updates instantly without
        // waiting for the refetch to round-trip. (WHA-3259)
        documentStore.applyRename(String(numericId), newFilename);
        await refetchDocuments();
        toast({
          title: t.documents.rename.success,
          description: t.documents.rename.successDescription.replace("{name}", newFilename),
        });
      } catch (error) {
        const description = (() => {
          if (error instanceof RenameDocumentValidationError) {
            const c = error.cause;
            switch (c.kind) {
              case "empty":
                return t.documents.rename.errorEmpty;
              case "tooLong":
                return t.documents.rename.errorTooLong.replace("{max}", String(c.max));
              case "invalidChars":
                return t.documents.rename.errorInvalidChars;
              case "extensionChanged":
                return t.documents.rename.errorExtensionChanged.replace(
                  "{ext}",
                  c.expected || "(none)"
                );
              case "collision":
                return t.documents.rename.errorCollision.replace("{name}", c.existingFilename);
              case "notFound":
                return t.documents.rename.errorNotFound;
              case "unknown":
                return c.message || t.documents.rename.errorUnknown;
            }
          }
          console.error("Rename error:", error);
          return t.documents.rename.errorUnknown;
        })();
        toast({
          variant: "destructive",
          title: t.documents.rename.errorTitle,
          description,
        });
      }
    },
    [documentStore, refetchDocuments, t, toast]
  );

  // Permission predicate for the rename action — applies per document.
  // Org-wide MODIFY_DATA passes; project members need MODIFY_PROJECT_FILES on
  // the document's project_id. RLS gates the actual write; this just hides
  // the UI affordance for users who would only get a server rejection.
  const canRenameDocument = useCallback(
    (doc: { project_id?: number | string | null }) => {
      if (canModifyData) return true;
      const projectId = doc.project_id;
      if (projectId === null || projectId === undefined || projectId === "") return false;
      const numeric = typeof projectId === "string" ? parseInt(projectId, 10) : projectId;
      if (Number.isNaN(numeric)) return false;
      return canModifyFilesInProject(numeric);
    },
    [canModifyData, canModifyFilesInProject]
  );

  const handleCategorizeDocument = useCallback(async (document: Document): Promise<string> => {
    console.log("Manual categorization requested for:", document.filename);
    return `Categorization initiated for ${document.filename}`;
  }, []);

  /**
   * Reload documents + folders. Every caller is an internal "the data just
   * changed" hook — upload complete, tag edit, folder rename, delete, sign —
   * and each of those already toasts its own outcome, so success stays silent.
   * It used to add a "Refreshed" toast on top, which meant two popups per
   * action. A failure does toast — see below.
   */
  const handleRefreshAll = useCallback(async () => {
    try {
      await Promise.all([
        refetchDocuments(),
        ...(buildingId
          ? [
              // Refetched from every view, not just List. The tree query is
              // `enabled` only on List, but this component stays mounted across
              // tabs and the client's staleTime is 5 minutes — so skipping the
              // refetch here does not defer it to the List remount, it serves
              // the pre-refresh tree until that window expires (restore a folder
              // from trash on Timeline, switch to List, it is still missing).
              // `refetch` ignores `enabled`, so it works from either tab.
              // `throwOnError` is what makes a folder failure reach the catch
              // below: react-query's refetch resolves with the error parked in
              // query state rather than rejecting, so without it the tree could
              // fail silently.
              refetchFolders({ throwOnError: true }),
              // Both counts stay on the same scope as the mount effect above —
              // dropping the project id here would silently widen the processing
              // badge (and the empty-state gate reading it) to the whole building
              // after the first refresh.
              documentStore.loadDocumentCount(buildingId, selectedProjectId ?? undefined),
              documentStore.refreshProcessingCount(buildingId, selectedProjectId ?? undefined),
            ]
          : []),
      ]);
    } catch (error) {
      // Most callers fire this and forget (`void onRefreshDocuments()`), so a
      // failed refetch would otherwise be an unhandled rejection that leaves
      // stale rows on screen with nothing said. Caught here rather than at each
      // call site so every one of them reports it the same way; awaiting callers
      // keep their own toast for the action that triggered the refresh.
      console.error("Refresh documents failed:", error);
      toast({ variant: "destructive", title: t.documents.list.error });
    }
  }, [refetchDocuments, refetchFolders, documentStore, buildingId, selectedProjectId, t, toast]);

  const trashOperations = useTrashOperations({
    buildingId,
    projectId: selectedProjectId ?? undefined,
    // Same refresh as everywhere else, and deliberately the *catching* one:
    // the hook awaits onRefresh inside the try that wraps the trash write, so
    // a refetch that rejects after a successful move would be reported as
    // "Move to trash failed" for a delete that went through.
    onRefresh: handleRefreshAll,
  });

  // Refetch trash data when switching to trash view
  useEffect(() => {
    if (showTrash) {
      trashOperations.refetch();
    }
  }, [showTrash]);

  const handleClearSearchResults = () => {
    setChatSearchResults(null);
  };

  const handleViewTrash = () => {
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.set("view", "trash");
    setSearchParams(newSearchParams);
  };

  const handleBackFromTrash = () => {
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.delete("view");
    setSearchParams(newSearchParams);
  };

  // Wrapper functions to match expected void return type
  const handleRestoreDocument = async (document: any) => {
    await trashOperations.handleRestoreDocument(document);
  };

  const handlePermanentDelete = async (document: any) => {
    await trashOperations.handlePermanentlyDelete(document);
  };

  const handleBulkPermanentDelete = async (documents: Document[]) => {
    await trashOperations.handleBulkPermanentlyDelete(documents);
  };

  const handleBulkRestoreDocuments = async (documents: Document[]) => {
    await trashOperations.handleBulkRestore(documents);
  };

  const handleEmptyTrash = async () => {
    await trashOperations.handleEmptyTrash();
  };

  // Fixed wrapper functions to match expected signatures
  const handleMoveDocuments = useCallback(
    async (docs: any[], targetFolder: string) => {
      try {
        const updates = docs.map((doc: any) => ({
          id: parseInt(doc.id),
          subcategory: targetFolder,
          category: "documents",
          modified_at: new Date().toISOString(),
        }));

        const { error } = await supabase.from("documents").upsert(updates);

        if (error) throw error;

        await refetchDocuments();
        toast({
          title: "Documents moved",
          description: `${docs.length} document(s) moved successfully`,
        });
        return true;
      } catch (error) {
        console.error("Move error:", error);
        toast({
          variant: "destructive",
          title: "Move failed",
          description: "Could not move the documents",
        });
        return false;
      }
    },
    [refetchDocuments, toast]
  );

  const handleFileDrop = useCallback((files: File[]) => {
    console.log("Files dropped for upload:", files.length);
  }, []);

  const handleAskFranklin = useCallback(
    (docs: Array<{ id: string; filename: string }>) => {
      docs.forEach((d) => chatStore.addAttachedDocument(d));
      chatStore.setViewMode("sidebar");
      documentStore.clearSelection();
    },
    [documentStore, chatStore]
  );

  // Show loading state while organization context is loading
  if (!isReady || !currentOrganization || !userProfile) {
    return (
      <div className="flex min-h-[200px] items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-primary"></div>
        <span className="ml-2 text-muted-foreground">Loading organisation context...</span>
      </div>
    );
  }

  if (showTrash) {
    return (
      <TrashView
        trashedDocuments={trashOperations.trashedDocuments}
        onRestore={handleRestoreDocument}
        onPermanentDelete={handlePermanentDelete}
        onBulkPermanentDelete={handleBulkPermanentDelete}
        onBulkRestore={handleBulkRestoreDocuments}
        onEmptyTrash={handleEmptyTrash}
        canPermanentlyDelete={canPermanentlyDeleteDocuments}
        isLoading={trashOperations.isLoading}
        onBack={handleBackFromTrash}
      />
    );
  }

  return (
    <div className="space-y-6">
      <DocumentsMainView
        buildingId={buildingId}
        buildingName={buildingName}
        documents={documents}
        folders={folders}
        isLoading={isLoading}
        searchQuery={filters.searchQuery}
        chatSearchResults={chatSearchResults}
        onDownloadDocument={handleDownloadDocument}
        onDeleteDocument={trashOperations.handleMoveToTrash}
        onRenameDocument={handleRenameDocument}
        canRenameDocument={canRenameDocument}
        onCategorizeDocument={handleCategorizeDocument}
        onMoveDocuments={handleMoveDocuments}
        onFileDrop={handleFileDrop}
        onClearSearchResults={handleClearSearchResults}
        onRefreshDocuments={handleRefreshAll}
        onViewTrash={handleViewTrash}
        selectedProjectId={selectedProjectId}
        currentPage={documentStore.currentPage}
        totalCount={documentStore.totalCount}
        pageSize={documentStore.pageSize}
        onPageChange={handlePageChange}
        onAskFranklin={handleAskFranklin}
        viewMode={viewMode}
        onManageShares={onManageShares}
      />
    </div>
  );
});
